export interface GlobalFavoritesExperience {
  title: "Saved deal" | "All Favorites";
  triggerLabel: "Saved deal" | "All favorites";
  countLabel: string;
  canClearAll: boolean;
  canUseStoreFeatures: boolean;
  emptyTitle: "No saved deal yet" | "No favorites yet";
  emptyDescription: string;
}

export function getGlobalFavoritesExperience(
  isFreeUser: boolean,
  favoritesCount: number,
): GlobalFavoritesExperience {
  if (isFreeUser) {
    return {
      title: "Saved deal",
      triggerLabel: "Saved deal",
      countLabel: favoritesCount === 1 ? "1 free save" : `${favoritesCount} free saves`,
      canClearAll: false,
      canUseStoreFeatures: false,
      emptyTitle: "No saved deal yet",
      emptyDescription: "Your free account includes one online saved deal. Save a deal with the heart button and it will show up here.",
    };
  }

  return {
    title: "All Favorites",
    triggerLabel: "All favorites",
    countLabel: String(favoritesCount),
    canClearAll: true,
    canUseStoreFeatures: true,
    emptyTitle: "No favorites yet",
    emptyDescription: "Save products with the heart button and they'll show up here across every retailer.",
  };
}

const FAVORITE_RETAILER_DOMAINS: Record<string, string> = {
  walmart: "walmart.com",
  walmart_online: "walmart.com",
  home_depot: "homedepot.com",
  target: "target.com",
  target_online: "target.com",
  bestbuy: "bestbuy.com",
  costco: "costco.com",
  amazon: "amazon.com",
  kohls: "kohls.com",
  woot: "woot.com",
  dollar_general: "dollargeneral.com",
  lowes: "lowes.com",
  tractor_supply: "tractorsupply.com",
  northern_tool: "northerntool.com",
  samsclub: "samsclub.com",
  ace_hardware: "acehardware.com",
  newegg: "newegg.com",
  gamestop: "gamestop.com",
  bjs: "bjs.com",
  meijer: "meijer.com",
  menards: "menards.com",
  academy: "academy.com",
  walgreens: "walgreens.com",
  zappos: "zappos.com",
  jcpenney: "jcpenney.com",
  belk: "belk.com",
  michaels: "michaels.com",
  nike: "nike.com",
  adidas: "adidas.com",
  reebok: "reebok.com",
  skechers: "skechers.com",
  puma: "us.puma.com",
  underarmour: "underarmour.com",
  columbia: "columbia.com",
  nordstromrack: "nordstromrack.com",
  aldo: "aldoshoes.com",
  vitacost: "vitacost.com",
  journeys: "journeys.com",
  shopdisney: "disneystore.com",
  ninjakitchen: "ninjakitchen.com",
  hottopic: "hottopic.com",
  officedepot: "officedepot.com",
  barnesnoble: "barnesandnoble.com",
  razer: "razer.com",
  pacsun: "pacsun.com",
  bathbodyworks: "bathandbodyworks.com",
  staples: "staples.com",
  petsmart: "petsmart.com",
  ulta: "ulta.com",
  forever21: "forever21.com",
  gap: "gap.com",
  petco: "petco.com",
  americaneagle: "ae.com",
  aerie: "ae.com",
  carters: "carters.com",
  abercrombie: "abercrombie.com",
  hollister: "hollisterco.com",
  oldnavy: "gap.com",
  bananarepublic: "gap.com",
  stevemadden: "stevemadden.com",
  athleta: "gap.com",
  gapfactory: "gapfactory.com",
  bananarepublicfactory: "gapfactory.com",
  boxlunch: "boxlunch.com",
  verabradley: "verabradley.com",
  footlocker: "footlocker.com",
  champs: "champssports.com",
};

function favoriteLookupUrl(retailer: string, productId: string): string | null {
  const id = encodeURIComponent(productId.trim());
  if (!id) return null;
  switch (retailer) {
    case "walmart":
    case "walmart_online": return `https://www.walmart.com/search?q=${id}`;
    case "home_depot": return `https://www.homedepot.com/s/${id}`;
    case "target":
    case "target_online": return `https://www.target.com/s?searchTerm=${id}`;
    case "bestbuy": return `https://www.bestbuy.com/site/searchpage.jsp?st=${id}`;
    case "costco": return `https://www.costco.com/CatalogSearch?dept=All&keyword=${id}`;
    case "amazon": return `https://www.amazon.com/s?k=${id}`;
    case "kohls": return `https://www.kohls.com/search.jsp?search=${id}`;
    case "woot": return "https://www.woot.com";
    case "dollar_general": return `https://www.dollargeneral.com/search?q=${id}`;
    case "lowes": return `https://www.lowes.com/search?searchTerm=${id}`;
    case "tractor_supply": return `https://www.tractorsupply.com/tsc/search/${id}`;
    case "northern_tool": return `https://www.northerntool.com/search?query=${id}`;
    case "samsclub": return `https://www.samsclub.com/ip/${id}`;
    case "ace_hardware": return `https://www.acehardware.com/search?query=${id}`;
    case "newegg": return `https://www.newegg.com/p/pl?d=${id}`;
    case "gamestop": return `https://www.gamestop.com/search/?q=${id}`;
    case "bjs": return `https://www.bjs.com/search/${id}`;
    case "meijer": return `https://www.meijer.com/shopping/search.html?text=${id}`;
    case "menards": return `https://www.menards.com/main/search.html?search=${id}`;
    case "academy": return `https://www.academy.com/search?q=${id}`;
    case "walgreens": return `https://www.walgreens.com/search/results.jsp?Ntt=${id}`;
    case "zappos": return `https://www.zappos.com/search?term=${id}`;
    case "jcpenney": return `https://www.jcpenney.com/s/${id}`;
    case "belk": return `https://www.belk.com/search/?q=${id}`;
    case "michaels": return `https://www.michaels.com/search?q=${id}`;
    case "nike": return `https://www.nike.com/w?q=${id}`;
    case "adidas": return `https://www.adidas.com/us/search?q=${id}`;
    case "reebok": return `https://www.reebok.com/search?q=${id}`;
    case "skechers": return `https://www.skechers.com/search?q=${id}`;
    case "puma": return `https://us.puma.com/us/en/search?q=${id}`;
    case "underarmour": return `https://www.underarmour.com/c/search/?q=${id}`;
    case "columbia": return `https://www.columbia.com/search?q=${id}`;
    case "nordstromrack": return `https://www.nordstromrack.com/s?q=${id}`;
    case "aldo": return `https://www.aldoshoes.com/search?q=${id}`;
    case "vitacost": return `https://www.vitacost.com/search?w=${id}`;
    case "journeys": return `https://www.journeys.com/search?q=${id}`;
    case "shopdisney": return `https://www.disneystore.com/search?q=${id}`;
    case "ninjakitchen": return `https://www.ninjakitchen.com/search?q=${id}`;
    case "hottopic": return `https://www.hottopic.com/search?q=${id}`;
    case "officedepot": return `https://www.officedepot.com/catalog/search.do?Ntt=${id}`;
    case "barnesnoble": return `https://www.barnesandnoble.com/s/${id}`;
    case "razer": return `https://www.razer.com/search?q=${id}`;
    case "pacsun": return `https://www.pacsun.com/search?q=${id}`;
    case "bathbodyworks": return `https://www.bathandbodyworks.com/search?q=${id}`;
    case "staples": return `https://www.staples.com/search?query=${id}`;
    case "petsmart": return `https://www.petsmart.com/search/?q=${id}`;
    case "ulta": return `https://www.ulta.com/search?search=${id}`;
    case "forever21": return `https://www.forever21.com/search?q=${id}`;
    case "gap": return `https://www.gap.com/browse/search.do?searchText=${id}`;
    case "petco": return `https://www.petco.com/shop/en/petcostore/search?query=${id}`;
    case "americaneagle": return `https://www.ae.com/us/en/s/${id}`;
    case "aerie": return `https://www.ae.com/us/en/s/${id}`;
    case "carters": return `https://www.carters.com/search?q=${id}`;
    case "abercrombie": return `https://www.abercrombie.com/shop/us/search?searchTerm=${id}`;
    case "hollister": return `https://www.hollisterco.com/shop/us/search?searchTerm=${id}`;
    case "oldnavy": return `https://oldnavy.gap.com/browse/search.do?searchText=${id}`;
    case "bananarepublic": return `https://bananarepublic.gap.com/browse/search.do?searchText=${id}`;
    case "stevemadden": return `https://www.stevemadden.com/search?q=${id}`;
    case "athleta": return `https://athleta.gap.com/browse/search.do?searchText=${id}`;
    case "gapfactory": return `https://www.gapfactory.com/browse/search.do?searchText=${id}`;
    case "bananarepublicfactory": return `https://bananarepublicfactory.gapfactory.com/browse/search.do?searchText=${id}`;
    case "boxlunch": return `https://www.boxlunch.com/search?q=${id}`;
    case "verabradley": return `https://verabradley.com/search?q=${id}`;
    case "footlocker": return `https://www.footlocker.com/search?query=${id}`;
    case "champs": return `https://www.champssports.com/search?query=${id}`;
    default: return null;
  }
}

/** Resolve a saved product to an HTTPS URL on its retailer's own domain. */
export function resolveFavoriteRetailerUrl(input: {
  candidateUrl?: string | null;
  retailer: string;
  productId?: string | null;
}): string | null {
  const retailer = input.retailer.trim().toLowerCase();
  const allowedDomain = FAVORITE_RETAILER_DOMAINS[retailer];
  if (!allowedDomain) return null;
  try {
    const parsed = new URL(input.candidateUrl || "");
    const hostname = parsed.hostname.toLowerCase().replace(/\.$/, "");
    if (
      parsed.protocol === "https:" &&
      !parsed.username &&
      !parsed.password &&
      (hostname === allowedDomain || hostname.endsWith(`.${allowedDomain}`))
    ) {
      return parsed.toString();
    }
  } catch {
    /* Use the retailer's own lookup page below. */
  }
  return favoriteLookupUrl(retailer, input.productId || "");
}
